import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Pencil, Link2, Lock, ShieldAlert } from "lucide-react";

const STATUS = ["active", "resigned", "inactive"];
const emptyEmp = { full_name: "", department_id: null, position_id: null, branch: "", employment_status: "active",
  employment_type: "", join_date: "", email: "", phone: "", mobile: "", is_packing: false,
  shift_group_id: null, active_in_schedule: true, sensitive: {} };

export default function EmployeesTab() {
  const { can } = useAuth();
  const canManage = can("hr:employee_manage");
  const canSensitive = can("hr:view_sensitive");
  const [emps, setEmps] = useState([]);
  const [depts, setDepts] = useState([]);
  const [positions, setPositions] = useState([]);
  const [shiftGroups, setShiftGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEmp);
  const [linkOpen, setLinkOpen] = useState(null);

  const load = () => api.get("/hr/employees").then(r => setEmps(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/hr/departments").then(r => setDepts(r.data)).catch(() => {});
    api.get("/hr/positions").then(r => setPositions(r.data)).catch(() => {});
    api.get("/hr/shift-groups").then(r => setShiftGroups(r.data)).catch(() => {});
    if (canManage) api.get("/hr/linkable-users").then(r => setUsers(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const deptName = (id) => depts.find(d => d.id === id)?.name || "—";
  const posName = (id) => positions.find(p => p.id === id)?.title || "—";

  const openCreate = () => { setEditing(null); setForm(emptyEmp); setOpen(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({ ...emptyEmp, ...e, sensitive: e.sensitive || {} });
    setOpen(true);
  };
  const save = async () => {
    const body = { ...form };
    if (!canSensitive) delete body.sensitive;
    try {
      if (editing) await api.put(`/hr/employees/${editing.id}`, body);
      else await api.post("/hr/employees", body);
      toast.success(editing ? "Employee updated" : `Employee created`);
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doLink = async (empId, userId) => {
    try { await api.put(`/hr/employees/${empId}/link-user`, { user_id: userId || null });
      toast.success("Link updated"); setLinkOpen(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setS = (k, v) => setForm(f => ({ ...f, sensitive: { ...f.sensitive, [k]: v } }));

  return (
    <div data-testid="hr-employees-tab">
      <div className="flex items-center justify-between mb-3">
        <p className="text-slate-500 text-sm">Employee IDs (FAS###) are auto-generated, permanent and locked. AutoCount stays an external reference.</p>
        {canManage && <Button onClick={openCreate} data-testid="employee-new-button" className="bg-sky-600 hover:bg-sky-700"><UserPlus className="w-4 h-4 mr-1.5" />Add Employee</Button>}
      </div>
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="excel-table w-full" data-testid="employees-table">
          <thead><tr>
            <th>FAS ID</th><th>Name</th><th>Department</th><th>Position</th><th>Status</th>
            <th>Packing</th><th>AutoCount</th><th>User Account</th>{canSensitive && <th>Salary</th>}<th></th>
          </tr></thead>
          <tbody>
            {emps.map(e => (
              <tr key={e.id} data-testid={`employee-row-${e.employee_id}`}>
                <td className="font-mono font-semibold flex items-center gap-1"><Lock className="w-3 h-3 text-slate-400" />{e.employee_id}</td>
                <td>{e.full_name}</td>
                <td>{deptName(e.department_id)}</td>
                <td>{posName(e.position_id)}</td>
                <td><Badge variant="outline" className={e.employment_status === "active" ? "text-emerald-700 border-emerald-300" : "text-slate-500"}>{e.employment_status}</Badge></td>
                <td>{e.is_packing ? "✓" : "—"}</td>
                <td className="font-mono text-xs">{e.external_autocount_employee_code || "—"}</td>
                <td className="text-xs" data-testid={`employee-linkstatus-${e.employee_id}`}>
                  {e.user_linked ? (
                    <span className="flex flex-col">
                      <Badge variant="outline" className="text-emerald-700 border-emerald-300 w-fit">User Linked</Badge>
                      <span className="text-slate-400 mt-0.5">{e.linked_user?.email}</span>
                    </span>
                  ) : <Badge variant="outline" className="text-slate-400 border-slate-200">No User Linked</Badge>}
                </td>
                {canSensitive && <td className="font-mono text-xs">{e.sensitive?.basic_salary != null ? e.sensitive.basic_salary : "—"}</td>}
                <td className="text-right whitespace-nowrap">
                  {canManage && <Button size="sm" variant="ghost" onClick={() => openEdit(e)} data-testid={`employee-edit-${e.employee_id}`}><Pencil className="w-4 h-4" /></Button>}
                  {canManage && <Button size="sm" variant="ghost" onClick={() => setLinkOpen(e)} data-testid={`employee-link-${e.employee_id}`}><Link2 className="w-4 h-4" /></Button>}
                </td>
              </tr>
            ))}
            {emps.length === 0 && <tr><td colSpan={canSensitive ? 10 : 9} className="text-center text-slate-400 py-8">No employees yet. Import from AutoCount or add manually.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? `Edit ${editing.employee_id}` : "New Employee"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Full name *</Label><Input data-testid="employee-name-input" value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} /></div>
            <div><Label>Department</Label>
              <Select value={form.department_id || "none"} onValueChange={v => setForm({ ...form, department_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="employee-dept-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{depts.map(d => <SelectItem key={d.id} value={d.id}>{d.kind === "team" ? "— " : ""}{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Position</Label>
              <Select value={form.position_id || "none"} onValueChange={v => setForm({ ...form, position_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="employee-pos-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{positions.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Employment status</Label>
              <Select value={form.employment_status} onValueChange={v => setForm({ ...form, employment_status: v })}>
                <SelectTrigger data-testid="employee-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Employment type</Label><Input value={form.employment_type || ""} onChange={e => setForm({ ...form, employment_type: e.target.value })} /></div>
            <div><Label>Branch</Label><Input value={form.branch || ""} onChange={e => setForm({ ...form, branch: e.target.value })} /></div>
            <div><Label>Join date</Label><Input type="date" value={(form.join_date || "").slice(0, 10)} onChange={e => setForm({ ...form, join_date: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Mobile</Label><Input value={form.mobile || ""} onChange={e => setForm({ ...form, mobile: e.target.value })} /></div>
            <div><Label>Shift group</Label>
              <Select value={form.shift_group_id || "none"} onValueChange={v => setForm({ ...form, shift_group_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="employee-shift-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{shiftGroups.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-6 col-span-2 pt-1">
              <label className="flex items-center gap-2 text-sm"><Switch checked={!!form.is_packing} onCheckedChange={v => setForm({ ...form, is_packing: v })} data-testid="employee-packing-switch" />Packing team member</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.active_in_schedule !== false} onCheckedChange={v => setForm({ ...form, active_in_schedule: v })} data-testid="employee-active-switch" />Active in schedule</label>
            </div>
            {canSensitive && (
              <div className="col-span-2 mt-2 border-t pt-3">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-600 mb-2"><ShieldAlert className="w-4 h-4" />Sensitive (restricted)</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Basic salary</Label><Input type="number" data-testid="employee-salary-input" value={form.sensitive?.basic_salary ?? ""} onChange={e => setS("basic_salary", e.target.value === "" ? null : parseFloat(e.target.value))} /></div>
                  <div><Label>IC / NRIC</Label><Input value={form.sensitive?.ic_no ?? ""} onChange={e => setS("ic_no", e.target.value)} /></div>
                  <div><Label>Bank account no</Label><Input value={form.sensitive?.bank_account_no ?? ""} onChange={e => setS("bank_account_no", e.target.value)} /></div>
                  <div><Label>EPF no</Label><Input value={form.sensitive?.epf_no ?? ""} onChange={e => setS("epf_no", e.target.value)} /></div>
                  <div><Label>SOCSO no</Label><Input value={form.sensitive?.socso_no ?? ""} onChange={e => setS("socso_no", e.target.value)} /></div>
                  <div><Label>Tax no</Label><Input value={form.sensitive?.tax_no ?? ""} onChange={e => setS("tax_no", e.target.value)} /></div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="employee-save-button" className="bg-slate-900">{editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link user dialog */}
      <Dialog open={!!linkOpen} onOpenChange={() => setLinkOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Link login account — {linkOpen?.employee_id}</DialogTitle>
            <DialogDescription>One employee links to at most one user account, and one user to at most one employee. Accounts already linked to someone else are disabled. Leave unlinked for staff who don't need an ERP login.</DialogDescription>
          </DialogHeader>
          <Label>User account</Label>
          <Select value={linkOpen?.user_id || "none"} onValueChange={v => doLink(linkOpen.id, v === "none" ? null : v)}>
            <SelectTrigger data-testid="employee-link-select"><SelectValue placeholder="Select user" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Unlink (no login) —</SelectItem>
              {users.map(u => {
                const takenByOther = u.linked_employee_id && u.linked_employee_id !== linkOpen?.id;
                return <SelectItem key={u.id} value={u.id} disabled={takenByOther}>
                  {u.email}{u.role ? ` · ${u.role}` : ""}{takenByOther ? ` · linked to ${u.linked_fas_id}` : ""}
                </SelectItem>;
              })}
            </SelectContent>
          </Select>
        </DialogContent>
      </Dialog>
    </div>
  );
}
